import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { authAxios } from "../../utils/auth";
import {
  RefreshCw, CheckCircle2, AlertTriangle, XCircle, Send, BookOpen,
  CreditCard, Mail, Database, Server, Sparkles, Cpu, Zap, Globe2, Shield
} from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  ok: { bg: "bg-emerald-500/10", ring: "ring-emerald-500/30", text: "text-emerald-400", Icon: CheckCircle2, label: "OK" },
  warn: { bg: "bg-amber-500/10", ring: "ring-amber-500/30", text: "text-amber-400", Icon: AlertTriangle, label: "WARN" },
  fail: { bg: "bg-red-500/10", ring: "ring-red-500/30", text: "text-red-400", Icon: XCircle, label: "FAIL" },
  skip: { bg: "bg-white/[0.04]", ring: "ring-white/10", text: "text-white/40", Icon: AlertTriangle, label: "SKIP" },
};

const SERVICE_ICON = {
  stripe: CreditCard,
  mailgun: Mail,
  mongodb: Database,
  redis: Server,
  arq: Zap,
  llm_keys: Sparkles,
  langfuse: Cpu,
  webhooks: Shield,
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.skip;
  const { Icon, label } = s;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ring-1 ${s.bg} ${s.ring} ${s.text}`}
          data-testid={`health-status-${status}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

function ServiceCard({ id, check }) {
  const Icon = SERVICE_ICON[id] || Server;
  const s = STATUS_STYLE[check.status] || STATUS_STYLE.skip;
  return (
    <div className={`rounded-xl border border-white/[0.06] bg-[#111113] p-4 hover:border-white/[0.12] transition-colors`}
         data-testid={`health-card-${id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${s.bg} ${s.text}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div>
            <div className="text-sm font-semibold text-white/90">{check.name}</div>
            <div className="text-[10px] text-white/30 uppercase tracking-wider">{check.category || "service"}</div>
          </div>
        </div>
        <StatusPill status={check.status} />
      </div>
      <div className="space-y-1.5 text-xs">
        {check.message && (
          <div className="text-white/60 leading-relaxed" data-testid={`health-msg-${id}`}>{check.message}</div>
        )}
        {check.details && Object.entries(check.details).map(([k, v]) => (
          <div key={k} className="flex justify-between gap-2 text-[11px]">
            <span className="text-white/30 truncate">{k}</span>
            <span className="text-white/70 font-mono truncate max-w-[60%] text-right">{String(v)}</span>
          </div>
        ))}
        <div className="flex items-center justify-between pt-2 border-t border-white/[0.04] mt-2">
          <div className="flex items-center gap-2">
            <span className="text-white/20 text-[10px]">latency</span>
            <span className="text-white/50 font-mono text-[10px]">{check.latency_ms != null ? `${check.latency_ms}ms` : "—"}</span>
          </div>
          {check.runbook && (
            <Link
              to={`/admin/runbook?section=${check.runbook}`}
              className="inline-flex items-center gap-1 text-[10px] text-[#C5A55A]/70 hover:text-[#C5A55A] transition-colors"
              data-testid={`runbook-link-${id}`}
              title="Open ops runbook for this service"
            >
              <BookOpen className="w-3 h-3" />
              runbook
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function EnvRow({ name, value, warning }) {
  return (
    <tr className={`border-b border-white/[0.04] ${warning ? "bg-amber-500/[0.03]" : ""}`} data-testid={`env-row-${name}`}>
      <td className="py-2 pr-3 text-xs text-white/60 font-mono">{name}</td>
      <td className="py-2 pr-3 text-xs text-white/80 font-mono break-all">{value ?? <span className="text-white/20">(unset)</span>}</td>
      <td className="py-2 text-xs">
        {warning && (
          <span className="inline-flex items-center gap-1 text-amber-400 text-[10px]" data-testid={`env-warn-${name}`}>
            <AlertTriangle className="w-3 h-3" /> {warning}
          </span>
        )}
      </td>
    </tr>
  );
}

export default function HealthCheckTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const timerRef = useRef(null);

  const runChecks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authAxios.get("/analytics/command/health");
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load health check");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    runChecks();
    // Health check is click-on-demand — the manual "Run Checks" button
    // (or a page reload) is the intended refresh path. The 30s auto-run
    // fired a heavy multi-probe every half-minute for every admin on
    // the page, chewing rate-limit budget for no meaningful benefit;
    // the "Last checked HH:MM" stamp already tells the admin how stale
    // the reading is.
  }, [runChecks]);

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const res = await authAxios.post("/analytics/command/health/test-email");
      if (res.data?.sent) {
        toast.success(`Test email sent to ${res.data.recipient}`);
      } else {
        toast.error(res.data?.message || "Failed to send test email");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  if (error) {
    return (
      <div className="rounded-xl border border-red-500/20 bg-red-500/[0.03] p-6" data-testid="health-error">
        <div className="flex items-center gap-2 text-red-400 mb-2">
          <XCircle className="w-5 h-5" />
          <span className="font-semibold">Health check failed to load</span>
        </div>
        <div className="text-sm text-white/60">{error}</div>
        <button
          onClick={runChecks}
          className="mt-4 px-3 py-1.5 text-xs bg-white/[0.05] hover:bg-white/[0.1] rounded-lg text-white/70 border border-white/10"
          data-testid="health-retry-btn"
        >Retry</button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="health-loading">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-white/[0.06] bg-[#111113] p-4 h-32 animate-pulse" />
        ))}
      </div>
    );
  }

  const { checks = {}, env = {}, environment, mismatches = [], generated_at, elapsed_ms } = data;
  const overall = Object.values(checks).some(c => c.status === "fail")
    ? "fail"
    : Object.values(checks).some(c => c.status === "warn") || mismatches.length > 0
      ? "warn"
      : "ok";

  return (
    <div className="space-y-6" data-testid="health-check-tab">
      {/* Overview bar */}
      <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <StatusPill status={overall} />
          <div>
            <div className="text-sm font-semibold text-white/90" data-testid="health-environment">
              Environment: <span className="text-[#C5A55A] uppercase">{environment?.mode || "unknown"}</span>
            </div>
            <div className="text-[11px] text-white/40 font-mono">
              host: {environment?.request_host || "—"} → frontend_url: {environment?.frontend_url || "—"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-white/30 font-mono hidden sm:inline">
            checked {generated_at ? new Date(generated_at).toLocaleTimeString() : "—"} • {elapsed_ms ?? "?"}ms
          </span>
          <button
            onClick={sendTestEmail}
            disabled={sendingTest || checks.mailgun?.status === "fail"}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-white/70 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="health-send-test-email-btn"
          >
            <Send className={`w-3.5 h-3.5 ${sendingTest ? "animate-pulse" : ""}`} />
            {sendingTest ? "Sending..." : "Send test email"}
          </button>
          <button
            onClick={runChecks}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-[#C5A55A]/10 hover:bg-[#C5A55A]/20 border border-[#C5A55A]/20 text-[#C5A55A] disabled:opacity-50"
            data-testid="health-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Re-run checks
          </button>
        </div>
      </section>

      {mismatches.length > 0 && (
        <section className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-4" data-testid="health-mismatches">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-amber-400 text-sm font-semibold">
              <AlertTriangle className="w-4 h-4" /> Configuration mismatches ({mismatches.length})
            </div>
            <Link
              to="/admin/runbook?section=runbook-env"
              className="inline-flex items-center gap-1 text-[11px] text-amber-400/80 hover:text-amber-300"
              data-testid="runbook-link-env"
            >
              <BookOpen className="w-3 h-3" /> runbook
            </Link>
          </div>
          <ul className="space-y-1.5 text-xs text-white/70 list-disc list-inside">
            {mismatches.map((m, i) => (<li key={i} data-testid={`mismatch-${i}`}>{m}</li>))}
          </ul>
        </section>
      )}

      {/* Service cards */}
      <section>
        <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-3">Live service checks</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(checks).map(([id, check]) => (
            <ServiceCard key={id} id={id} check={check} />
          ))}
        </div>
      </section>

      {/* Env variables */}
      <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-5" data-testid="health-env-vars">
        <div className="flex items-center gap-2 mb-4">
          <Globe2 className="w-4 h-4 text-white/40" />
          <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wider">Environment variables</h3>
          <span className="text-[10px] text-white/30 ml-auto font-mono">secrets masked (last 4 shown)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/10 text-left text-white/40 uppercase tracking-wider text-[10px]">
                <th className="pb-2 pr-3 font-medium">Variable</th>
                <th className="pb-2 pr-3 font-medium">Value</th>
                <th className="pb-2 font-medium">Notes</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(env).map(([k, v]) => {
                const val = typeof v === "object" ? v?.value : v;
                const warn = typeof v === "object" ? v?.warning : null;
                return <EnvRow key={k} name={k} value={val} warning={warn} />;
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
